/**
 * buildQuote — DETERMINISTIC: a request + the live catalog → a priced quote + flags.
 *
 * Ported from quote-gen `quote_build.build_quote`
 * (~/optisigns/os-hubspot-copilot/skills/quote-gen/scripts/quote_build.py). Same inputs
 * in → same model out; no network, no clock, no randomness. The operator's form is the
 * only input, and EVERY price comes from the catalog — there is no override path. The
 * operator chooses what to quote and how much to discount it; they cannot choose the rate.
 *
 * The rules below come from the 1,509-ticket quote/PO SOP sweep — see
 * `skills/quote-gen/references/quote-rules.md`. Do not "improve" one without changing it
 * there too, or a staff quote and an OptiClaw quote for the same request will disagree.
 *
 * One deliberate difference from the Python: upstream this engine is fail-CLOSED because
 * nothing human sees the quote before it is sent. Here a human IS the operator, so flags
 * surface as on-screen warnings and only an unpriced line actually blocks printing.
 */
import {
  CURRENCY_SET,
  defaultShipping,
  inferRegion,
  matchProduct,
  resolveProductPrice,
  resolveShipping,
} from './catalog';
import { billableUnits, isOneTime, matchAddOn, resolveAddOnPrice, resolveTier } from './addons';
import { toCents } from './cents';
import {
  ENTERPRISE_ALIAS,
  ENTERPRISE_MIN_SCREENS,
  matchPlan,
  normalizeTerm,
  resolvePlanPrice,
} from './plans';
import type { PricedLine, PricedQuote, QuoteCatalog, QuoteRequest } from './types';

export function buildQuote(request: QuoteRequest, catalog: QuoteCatalog): PricedQuote {
  const { products, shippings, plans: planCatalog, addOns } = catalog;
  const currency = (request.currency || 'usd').toLowerCase();
  const hitl: string[] = [];
  const lines: PricedLine[] = [];

  if (!CURRENCY_SET.has(currency)) {
    hitl.push(`unknown currency '${currency}' (expected one of ${[...CURRENCY_SET].join(', ')})`);
  }

  const defaultRegion = inferRegion(request.customer?.country);

  request.lines.forEach((item, i) => {
    const fallbackLabel =
      item.query || item.sku || `item #${i + 1}`;
    let qty = item.quantity;
    if (typeof qty !== 'number' || !Number.isInteger(qty) || qty <= 0) {
      hitl.push(`line '${fallbackLabel}': missing or invalid quantity`);
      qty = typeof qty === 'number' && Number.isInteger(qty) ? qty : 0;
    }

    if (item.kind === 'shipping') {
      const m = matchProduct(item.query, item.sku, shippings);
      const doc = m.match;
      if (!doc) {
        hitl.push(`shipping '${fallbackLabel}': ${m.reason} — no catalog match`);
        lines.push({
          rowId: item.id,
          kind: 'shipping',
          label: fallbackLabel,
          quantity: qty,
          unitCents: null,
          amountCents: null,
        });
        return;
      }

      const region = item.region || defaultRegion;
      const s = resolveShipping(doc, region, currency);
      if (!s.ok) {
        hitl.push(`shipping '${doc.name ?? doc._id}': ${s.error}`);
        lines.push({
          rowId: item.id,
          kind: 'shipping',
          label: doc.name ?? doc._id,
          sku: doc._id,
          quantity: qty,
          region,
          unitCents: null,
          amountCents: null,
        });
        return;
      }
      if (s.fallback) {
        hitl.push(
          `shipping '${doc.name ?? doc._id}': currency '${currency}' missing → used usd rate`,
        );
      }

      // first + addl × (qty − 1). Where there's no first-device rate the two are equal,
      // so this reduces to a flat per-unit charge. Both rates are numbers here — the
      // `!s.ok` branch above absorbed every unpriced case.
      const amount = qty > 0 ? s.firstCents + s.addlCents * (qty - 1) : null;
      lines.push({
        rowId: item.id,
        kind: 'shipping',
        label: doc.name ?? doc._id,
        sku: doc._id,
        quantity: qty,
        region,
        unitCents: s.addlCents,
        shippingFirstCents: s.firstCents,
        amountCents: amount,
      });
      return;
    }

    if (item.kind === 'device') {
      const m = matchProduct(item.query, item.sku, products);
      const doc = m.match;
      if (!doc) {
        hitl.push(`line '${fallbackLabel}': ${m.reason} — no catalog match`);
        lines.push({
          rowId: item.id,
          kind: 'device',
          label: fallbackLabel,
          quantity: qty,
          unitCents: null,
          amountCents: null,
        });
        return;
      }

      const r = resolveProductPrice(doc, currency);
      const unit = r.cents;
      const priceFallback = r.fallback;
      if (unit === null) {
        hitl.push(`line '${fallbackLabel}': no price for currency '${currency}' and no fallback`);
      } else if (priceFallback) {
        hitl.push(
          `line '${doc.name ?? fallbackLabel}': currency '${currency}' missing → used the USD list price`,
        );
      }

      lines.push({
        rowId: item.id,
        kind: 'device',
        label: doc.name ?? fallbackLabel,
        sku: doc._id,
        variantId: doc.variantId,
        quantity: qty,
        unitCents: unit,
        amountCents: unit !== null ? unit * qty : null,
        priceFallback,
      });
      return;
    }

    if (item.kind === 'addon') {
      const requestedTerm = normalizeTerm(item.term);

      let label = fallbackLabel;
      let sku: string | undefined;
      let unit: number | null = null;
      let tierKey: string | undefined;
      let units = qty;

      const am = matchAddOn(item.query, item.sku, addOns);
      const adoc = am.match;

      if (adoc) {
        sku = adoc.key;
        // A tiered add-on is a different PRODUCT per tier, so the tier belongs in the
        // label: "OptiSound Music" alone would not tell a customer which one they bought.
        const { tier, unknownRequest } = resolveTier(adoc, item.tier);
        tierKey = tier?.key;
        label = adoc.name ?? label;
        if (tier) label = `${label} ${tier.name ?? tier.key}`;
        if (unknownRequest) {
          hitl.push(
            `add-on '${adoc.name ?? adoc.key}': no tier '${item.tier}' — priced the ${tier?.name ?? tier?.key} tier instead`,
          );
        }

        units = billableUnits(adoc, qty);
        if (isOneTime(adoc) && qty > 1) {
          hitl.push(
            `add-on '${label}': one-time charge, so the quantity of ${qty} does not multiply it`,
          );
        } else if (units > qty) {
          // OptiDev's 3-screen floor. Bill the floor AND say why, so the figure is right
          // and the operator can see it differs from what they typed.
          hitl.push(
            `add-on '${label}': sold in a minimum of ${adoc.minQuantity}, so ${units} are billed rather than ${qty}`,
          );
        }
      }

      {
        if (!addOns.length) {
          hitl.push(`add-on '${label}': no add-on catalog loaded — check SCIO_ENV`);
        } else if (!adoc) {
          hitl.push(`add-on '${label}': ${am.reason}`);
        } else {
          const r = resolveAddOnPrice(adoc, currency, requestedTerm, item.tier);
          unit = r.cents;
          if (r.unavailableIn) {
            // Not a missing price — the add-on genuinely isn't sold in this currency, so
            // there is nothing to fall back to.
            hitl.push(
              `add-on '${label}': not sold in '${r.unavailableIn}' — only ${(adoc.limitCurrencies ?? []).join(', ')}`,
            );
          } else if (unit === null) {
            hitl.push(
              `add-on '${label}': no price for '${r.priceKey}' / currency '${currency}'`,
            );
          } else if (r.fallback) {
            // For an auto-convert add-on this is no longer the routine path — its non-USD
            // rate should have come from Stripe. Reaching here means Stripe had nothing
            // for it (OptiDev's unconfigured tiers) or could not be read, and the figure
            // printed is dollars behind the wrong symbol. Say which, so the operator can
            // tell a data gap from an outage.
            hitl.push(
              adoc.isAutoConvertCurrency
                ? `add-on '${label}': no Stripe rate for '${currency}' — quoted at the USD list price, which is NOT a converted figure`
                : `add-on '${label}': currency '${currency}' missing → used the USD price`,
            );
          }
        }
      }

      const multiplier = units;

      lines.push({
        rowId: item.id,
        kind: 'addon',
        label,
        sku,
        quantity: qty,
        unitCents: unit,
        amountCents: unit !== null ? unit * multiplier : null,
        term: requestedTerm,
        tier: tierKey,
      });
      return;
    }

    if (item.kind === 'subscription') {
      const requestedTerm = normalizeTerm(item.term);

      let label = fallbackLabel;
      let sku: string | undefined;
      let stripePlanId: string | null = null;
      let unit: number | null = null;

      if (!planCatalog.length) {
        hitl.push(`subscription '${label}': no plan catalog loaded — check SCIO_ENV`);
      } else {
        const pm = matchPlan(item.query, item.sku, planCatalog);
        const pdoc = pm.match;
        if (!pdoc) {
          hitl.push(`subscription '${label}': ${pm.reason} — no plan match`);
        } else {
          const r = resolvePlanPrice(pdoc, currency, requestedTerm);
          label = pdoc.name ?? label;
          sku = pdoc._id;
          unit = r.cents;
          stripePlanId = r.stripePlanId;
          if (pdoc.alias === ENTERPRISE_ALIAS && unit !== null && r.fallback) {
            // Enterprise is priced from Stripe per currency. A dollar figure behind another
            // currency's symbol is a wrong quote, so a missing currency is unpriced instead.
            hitl.push(`subscription '${label}': not priced in ${currency.toUpperCase()} — quote it in USD`);
            unit = null;
          } else if (pdoc.alias === ENTERPRISE_ALIAS && unit !== null && qty < ENTERPRISE_MIN_SCREENS) {
            hitl.push(`subscription '${label}': sold to ${ENTERPRISE_MIN_SCREENS} screens or more`);
            unit = null;
          } else if (unit === null) {
            hitl.push(
              `subscription '${label}': no price for term '${r.term}' / currency '${currency}'`,
            );
          } else if (r.fallback) {
            hitl.push(
              `subscription '${label}': currency '${currency}' missing → used the USD price`,
            );
          }
        }
      }

      lines.push({
        rowId: item.id,
        kind: 'subscription',
        label,
        sku,
        quantity: qty,
        unitCents: unit,
        amountCents: unit !== null ? unit * qty : null,
        term: requestedTerm,
        stripePlanId,
      });
      return;
    }

    // Unreachable through the typed form, but kept as a runtime backstop: `lines` can
    // also arrive from a saved/pasted payload, and an unknown kind must fail visibly
    // rather than silently drop a line the operator believes they added.
    const unknownKind: string = item.kind;
    hitl.push(`line '${fallbackLabel}': unknown kind '${unknownKind}'`);
    lines.push({
      rowId: item.id,
      kind: 'device',
      label: fallbackLabel,
      quantity: qty,
      unitCents: null,
      amountCents: null,
    });
  });

  // ── Auto-add shipping when the quote has hardware and no shipping line ──
  // SOP: a hardware quote includes shipping. An EXPLICIT shipping line (the customer
  // asked for Express) already sits in `lines` and overrides this — never double-added.
  // Subscription-only quotes get no shipping.
  const hwQty = lines
    .filter((l) => l.kind === 'device' && l.amountCents !== null && l.quantity > 0)
    .reduce((sum, l) => sum + l.quantity, 0);

  if (hwQty > 0 && !lines.some((l) => l.kind === 'shipping')) {
    const sdoc = defaultShipping(shippings, 'standard');
    if (!sdoc) {
      hitl.push('shipping: no Standard shipping option in the catalog — add shipping by hand');
    } else {
      const s = resolveShipping(sdoc, defaultRegion, currency);
      // resolveShipping reports an unusable rate as `error`, so this one branch covers
      // both a missing region and a region with nothing priced.
      if (!s.ok) {
        hitl.push(`shipping (auto): ${s.error} — add shipping by hand`);
      } else {
        if (s.fallback) {
          hitl.push(
            `shipping '${sdoc.name ?? sdoc._id}': currency '${currency}' missing → used usd rate`,
          );
        }
        lines.push({
          kind: 'shipping',
          label: sdoc.name ?? sdoc._id,
          sku: sdoc._id,
          quantity: hwQty,
          region: defaultRegion,
          unitCents: s.addlCents,
          shippingFirstCents: s.firstCents,
          amountCents: s.firstCents + s.addlCents * (hwQty - 1),
          autoAdded: true,
        });
      }
    }
  }

  // ── Discounts: per line, subscriptions and add-ons ONLY ──
  // Applied here rather than inside each branch so there is ONE place the rule lives, and
  // one place to look when a printed figure is questioned.
  const DISCOUNTABLE = new Set(['subscription', 'addon']);
  const discounted: string[] = [];

  request.lines.forEach((item) => {
    const raw = item.discountPct;
    if (raw === undefined || raw === null || raw === '') return;

    const pct = typeof raw === 'number' ? raw : Number(raw);
    const label = item.query || item.sku || 'line';

    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      hitl.push(`line '${label}': discount '${raw}' is not a percentage between 0 and 100 — ignored`);
      return;
    }

    // Hardware and shipping are never discounted. The form doesn't offer the field on
    // those rows, so reaching this means a pasted or linked payload asked for it.
    if (!DISCOUNTABLE.has(item.kind)) {
      hitl.push(
        `line '${label}': a ${pct}% discount was requested on a ${item.kind} line — discounts apply to subscriptions and add-ons only, so it was NOT applied`,
      );
      return;
    }

    const priced = lines.find((l) => l.rowId === item.id);
    if (!priced || priced.amountCents === null) {
      hitl.push(`line '${label}': ${pct}% discount could not be applied — the line has no price`);
      return;
    }

    priced.discountPct = pct;
    priced.discountCents = Math.round((priced.amountCents * pct) / 100);
    discounted.push(`${priced.label} ${pct}%`);
  });

  const discountCents = lines.reduce((sum, l) => sum + (l.discountCents ?? 0), 0);
  const discountNote = discounted.length ? `Discounts: ${discounted.join(', ')}` : null;

  const subtotalCents = lines.reduce((sum, l) => sum + (l.amountCents ?? 0), 0);
  const priceable = lines.length > 0 && lines.every((l) => l.amountCents !== null);

  return {
    ready: priceable && hitl.length === 0,
    hitl,
    currency,
    customer: request.customer ?? {},
    lines,
    subtotalCents,
    discountCents,
    discountNote,
    totalCents: subtotalCents - discountCents,
  };
}
